import { afterNextRender, Component, computed, DestroyRef, effect, ElementRef, inject, signal, viewChild } from '@angular/core';

/**
 * @param {HTMLElement} element - Where to start looking
 * @returns {HTMLElement | null} The nearest ancestor that scrolls vertically, or null for the page
 */
function findScrollPort(element: HTMLElement): HTMLElement | null {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    // A hidden overflow establishes a scroll container too, and sticky resolves against it.
    if (/auto|scroll|overlay|hidden/.test(getComputedStyle(parent).overflowY)) {
      return parent;
    }
  }
  return null;
}

/**
 * What the port actually shows. A sticky offset is resolved against the scrollport, which is the
 * padding box — the same box `clientHeight` measures — so its padding is already accounted for.
 * Taking the padding off again put the offset that much too low and pinned a tall column short
 * of its own end.
 *
 * @param {HTMLElement | null} scrollPort - The scrolling ancestor, or null for the page
 * @returns {number} The height the column has to fit into
 */
function readVisibleHeight(scrollPort: HTMLElement | null): number {
  return scrollPort ? scrollPort.clientHeight : window.innerHeight;
}

/**
 * Two-column layout of a detail view: a wide main column and a narrower
 * properties column beside it.
 *
 * Three slots, projected by attribute:
 * - `lead` — content above everything else, in the main column once split
 * - `aside` — the properties column
 * - the default slot — the rest of the main column
 *
 * While narrow the three stack as *lead → aside → main*, so the properties sit
 * directly under the title instead of below a long history.
 *
 * The split is a container query on this component's own width, so it stacks
 * inside a narrow drawer while a full-width page at the same viewport splits.
 * `dm-block` is part of that: `container-type` does nothing on the inline box a
 * custom element renders as by default.
 *
 * Split, the properties column is sticky, so a long main column scrolls past it
 * rather than leaving it behind. A column taller than what is on screen travels
 * with the page until its end shows and pins from there, which is why both it
 * and its scrolling ancestor are measured: the overhang between them is the
 * offset, and CSS can express neither.
 */
@Component({
  selector: 'lp-detail-split-layout',
  templateUrl: './detail-split-layout.component.html',
  styleUrl: './detail-split-layout.component.scss',
  host: { class: 'dm-block dm-container' },
})
export class DetailSplitLayoutComponent {
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly asideColumn = viewChild.required<ElementRef<HTMLElement>>('asideColumn');

  /** What the scrolling ancestor shows at once, its padding already taken off. */
  private readonly visiblePortHeight = signal<number>(0);
  private readonly asideHeight = signal<number>(0);

  /** Zero while the column fits, and its overhang — a negative offset — while it does not. */
  private readonly stickyTop = computed<number>(() => Math.min(0, this.visiblePortHeight() - this.asideHeight()));

  private readonly stickyTopEffect = effect(() => {
    this.hostElement.nativeElement.style.setProperty(this.asideTopProperty, `${this.stickyTop()}px`);
  });

  /** The property the stylesheet offsets the sticky column by. */
  private readonly asideTopProperty = '--lp-detail-split-layout-aside-top';

  constructor() {
    // The host has to be in the document before its scrolling ancestor can be found.
    afterNextRender(() => {
      const aside = this.asideColumn().nativeElement;
      const scrollPort = findScrollPort(this.hostElement.nativeElement);

      const observer = new ResizeObserver(() => {
        this.asideHeight.set(aside.offsetHeight);
        this.visiblePortHeight.set(readVisibleHeight(scrollPort));
      });
      observer.observe(aside);
      if (scrollPort) {
        observer.observe(scrollPort);
        this.destroyRef.onDestroy(() => observer.disconnect());
        return;
      }

      // Without a scrolling ancestor the port is the window, which the observer cannot watch:
      // a resize that leaves the column's own height alone would never reach the measurement.
      const onResize = (): void => this.visiblePortHeight.set(readVisibleHeight(null));
      window.addEventListener('resize', onResize);
      this.destroyRef.onDestroy(() => {
        observer.disconnect();
        window.removeEventListener('resize', onResize);
      });
    });
  }
}
